// ============================================================================
// SYSTELOS - PageBanner Component
// ============================================================================
// Banner padrão de página com gradiente azul, título, subtítulo e ações
// Versão: 1.2 | Atualizado: 23/01/2026
// ============================================================================

import { ReactNode, ButtonHTMLAttributes } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export interface PageBannerProps {
  /** Título principal da página */
  titulo: string;
  /** Subtítulo/descrição (opcional) */
  subtitulo?: string;
  /** Ícone (ReactNode - pode ser <Icon /> ou componente) */
  icon?: ReactNode;
  /** Conteúdo adicional (badges, indicadores, etc) */
  children?: ReactNode;
  /** Botões de ação (usar BotaoPrimario e BotaoSecundario) */
  acoes?: ReactNode;
  /** Classes CSS adicionais */
  className?: string;
}

interface BotaoBaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Ícone (ReactNode) */
  icon?: ReactNode;
  /** Conteúdo do botão */
  children: ReactNode;
  /** Estado de loading */
  loading?: boolean;
}

// ============================================================================
// STYLES CONSTANTS
// ============================================================================

const BANNER_STYLES = {
  background: 'linear-gradient(135deg, #0F1B3D 0%, #1a2d5a 100%)',
  boxShadow: '0 10px 40px rgba(15, 27, 61, 0.3)',
};

const TITULO_STYLES = {
  fontFamily: 'Poppins, sans-serif',
  color: '#FFFFFF',
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Botão primário (amarelo) para ação principal
 */
function BotaoPrimario({ 
  icon, 
  children, 
  loading = false,
  disabled,
  className = '',
  ...props 
}: BotaoBaseProps) {
  return (
    <button
      className={`
        flex items-center gap-2 
        px-4 py-2 
        rounded-lg 
        font-medium 
        transition-all 
        hover:scale-105
        disabled:opacity-50 
        disabled:cursor-not-allowed
        disabled:hover:scale-100
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      style={{ 
        backgroundColor: '#FFB800', 
        color: '#0F1B3D' 
      }}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <svg 
          className="w-4 h-4 animate-spin" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : icon ? (
        <span className="w-4 h-4 flex items-center justify-center">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}

/**
 * Botão secundário (transparente) para ações secundárias
 */
function BotaoSecundario({ 
  icon, 
  children, 
  loading = false,
  disabled,
  className = '',
  ...props 
}: BotaoBaseProps) {
  return (
    <button
      className={`
        flex items-center gap-2 
        px-4 py-2 
        bg-white/10 
        text-white 
        hover:bg-white/20 
        rounded-lg 
        transition-colors
        disabled:opacity-50 
        disabled:cursor-not-allowed
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <svg 
          className="w-4 h-4 animate-spin" 
          fill="none" 
          viewBox="0 0 24 24"
        >
          <circle 
            className="opacity-25" 
            cx="12" 
            cy="12" 
            r="10" 
            stroke="currentColor" 
            strokeWidth="4"
          />
          <path 
            className="opacity-75" 
            fill="currentColor" 
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : icon ? (
        <span className="w-4 h-4 flex items-center justify-center">{icon}</span>
      ) : null}
      {children}
    </button>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PageBanner({
  titulo,
  subtitulo,
  icon,
  children,
  acoes,
  className = '',
}: PageBannerProps) {
  return (
    <div 
      className={`rounded-2xl p-6 md:p-8 mb-6 ${className}`}
      style={BANNER_STYLES}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Título e Subtítulo */}
        <div>
          <div className="flex items-center gap-3">
            {/* Ícone opcional */}
            {icon && (
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-white">
                {icon}
              </div>
            )}
            <div>
              <h1 
                className="text-2xl md:text-3xl font-bold mb-1"
                style={TITULO_STYLES}
              >
                {titulo}
              </h1>
              {subtitulo && (
                <p className="text-white/70 text-sm">
                  {subtitulo}
                </p>
              )}
            </div>
          </div>
          {/* Conteúdo adicional (children) */}
          {children && (
            <div className="mt-3">
              {children}
            </div>
          )}
        </div>

        {/* Ações */}
        {acoes && (
          <div className="flex items-center gap-3">
            {acoes}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPOUND COMPONENTS
// ============================================================================

PageBanner.BotaoPrimario = BotaoPrimario;
PageBanner.BotaoSecundario = BotaoSecundario;

export default PageBanner;

